import { useEffect, useState } from "react";
import { UserPlus, KeyRound, Trash2, Copy, ShieldCheck, ScanLine, Ban, CheckCircle2, Shield, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export const PERMS = [
  ["review_invoices", "Review & confirm invoices", "Edit, confirm, unlock and delete invoices"],
  ["manage_suppliers", "Suppliers & products", "Edit suppliers, product matching"],
  ["view_prices", "Price comparison", "See supplier price comparison dashboard"],
  ["manage_team", "Manage staff", "Add, reset or remove staff members (not admins/owners)"],
  ["manage_settings", "Organisation settings", "Restaurants, addresses, organisation name"],
];
const ROLE_META = {
  owner: { label: "Owner · full access", icon: ShieldCheck, cls: "bg-slate-100 border-slate-300 text-slate-800", avatar: "bg-slate-900 text-emerald-300" },
  admin: { label: "Admin", icon: Shield, cls: "bg-violet-50 border-violet-200 text-violet-800", avatar: "bg-violet-100 text-violet-700" },
  staff: { label: "Staff · scan & order", icon: ScanLine, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", avatar: "bg-emerald-50 text-emerald-700" },
};
const EMPTY = { name: "", email: "", password: "", role: "staff", restaurant_ids: [], permissions: Object.fromEntries(PERMS.map(([k]) => [k, true])) };

function Credentials({ cred, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(`Supplivo login\nEmail: ${cred.email}\nTemporary password: ${cred.password}\n${window.location.origin}/login`); toast.success("Copied"); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="credentials-dialog">
        <DialogHeader><DialogTitle>Share these login details</DialogTitle><DialogDescription>They'll be asked to choose their own password after signing in. This temporary password is shown only once.</DialogDescription></DialogHeader>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 font-mono text-sm space-y-1">
          <p>Email: <span data-testid="cred-email">{cred.email}</span></p>
          <p>Password: <span className="font-semibold" data-testid="cred-password">{cred.password}</span></p>
          <p className="text-slate-500 text-xs">{window.location.origin}/login</p>
        </div>
        <Button onClick={copy} className="w-full h-11 bg-slate-900" data-testid="copy-credentials-button"><Copy className="h-4 w-4 mr-2" /> Copy details</Button>
      </DialogContent>
    </Dialog>
  );
}

function PermissionToggles({ value, onChange, idPrefix, disabled }) {
  return (
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" data-testid={`${idPrefix}-permissions`}>
      {PERMS.map(([k, l, d]) => (
        <li key={k} className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="min-w-0"><span className="block text-sm font-medium text-slate-800">{l}</span><span className="block text-xs text-slate-500">{d}</span></span>
          <Switch checked={value?.[k] !== false} disabled={disabled} onCheckedChange={(v) => onChange(k, v)} data-testid={`${idPrefix}-perm-${k}`} />
        </li>
      ))}
    </ul>
  );
}

function RestaurantPicker({ m, restaurants, onClose, onSaved }) {
  const [picked, setPicked] = useState(m.restaurant_ids || []);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/team/${m.id}`, { restaurant_ids: picked });
      toast.success(picked.length ? `${m.name} can now access ${picked.length} site${picked.length === 1 ? "" : "s"}` : `${m.name} can now access every site`);
      onSaved();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={`sites-dialog-${m.id}`}>
        <DialogHeader>
          <DialogTitle>Site access · {m.name}</DialogTitle>
          <DialogDescription>Pick the sites {m.name} can see and work on. No selection means every site — you can change this any time.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 py-2">
          {restaurants.map((r) => (
            <button type="button" key={r.id} onClick={() => toggle(r.id)} data-testid={`sites-dialog-restaurant-${r.id}`} className={`rounded-full border px-3 py-1.5 text-sm ${picked.includes(r.id) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>{r.name}</button>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid={`sites-dialog-cancel-${m.id}`}>Cancel</Button>
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={busy} data-testid={`sites-dialog-save-${m.id}`}>{busy ? "Saving…" : "Save access"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MemberCard({ m, me, isTrueOwner, restaurants, onAction, onEditSites }) {
  const isMe = m.id === me.id;
  const meta = ROLE_META[m.role] || ROLE_META.staff;
  const canManage = !isMe && (isTrueOwner || m.role === "staff");
  const rest = m.restaurant_ids?.length ? restaurants.filter((r) => m.restaurant_ids.includes(r.id)).map((r) => r.name).join(", ") : "All restaurants";
  const enabled = m.role === "admin" ? PERMS.filter(([k]) => m.permissions?.[k] !== false).length : null;
  return (
    <div className={`bg-white border rounded-lg p-4 ${m.is_active === false ? "border-slate-200 opacity-60" : "border-slate-200"}`} data-testid={`member-card-${m.id}`}>
      <div className="flex items-start gap-3">
        <div className={`h-11 w-11 rounded-full grid place-items-center font-semibold shrink-0 ${meta.avatar}`}>{(m.name || m.email).slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">{m.name} {isMe && <span className="text-xs text-slate-400 font-normal">(you)</span>}</p>
          <p className="text-xs text-slate-500 truncate">{m.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${meta.cls}`} data-testid={`member-role-${m.id}`}>
              <meta.icon className="h-3 w-3" /> {meta.label}{enabled != null ? ` · ${enabled}/${PERMS.length} permissions` : ""}
            </span>
            {m.is_active === false && <span className="rounded-full border border-rose-200 bg-rose-50 text-rose-700 px-2 py-0.5">Deactivated</span>}
            <span className="text-slate-400">{rest}</span>
            <span className="text-slate-400">· {m.invoice_count || 0} invoices</span>
          </div>
        </div>
      </div>
      {canManage && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {isTrueOwner ? (
            <select value={m.role} onChange={(e) => onAction("role", m, e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`member-role-select-${m.id}`}>
              <option value="staff">Staff</option><option value="admin">Admin</option><option value="owner">Owner</option>
            </select>
          ) : <span className="h-10 grid place-items-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-md">Role: owner only</span>}
          {restaurants.length > 1 ? (
            <Button variant="outline" size="sm" className="h-10" onClick={() => onEditSites(m)} data-testid={`member-sites-${m.id}`}><Building2 className="h-4 w-4 mr-1" /> Sites</Button>
          ) : <span />}
          <Button variant="outline" size="sm" className="h-10" onClick={() => onAction("reset", m)} data-testid={`member-reset-${m.id}`}><KeyRound className="h-4 w-4 mr-1" /> New password</Button>
          <Button variant="outline" size="sm" className="h-10" onClick={() => onAction("active", m)} data-testid={`member-toggle-active-${m.id}`}>{m.is_active === false ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Activate</> : <><Ban className="h-4 w-4 mr-1" /> Deactivate</>}</Button>
          <Button variant="outline" size="sm" className="h-10 text-rose-600 hover:text-rose-700" onClick={() => onAction("remove", m)} data-testid={`member-remove-${m.id}`}><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>
        </div>
      )}
      {m.role === "admin" && isTrueOwner && !isMe && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">What this admin can do</p>
          <PermissionToggles value={m.permissions} onChange={(k, v) => onAction("perm", m, { [k]: v })} idPrefix={`member-${m.id}`} />
        </div>
      )}
    </div>
  );
}

export default function Team() {
  const { user, restaurants, isTrueOwner } = useAuth();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [cred, setCred] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editSites, setEditSites] = useState(null);

  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, password: form.password || null, permissions: form.role === "admin" ? form.permissions : null };
      const { data } = await api.post("/team", body);
      setOpen(false); setForm(EMPTY); load();
      const gmail = /@(gmail|googlemail)\.com$/i.test(data.member.email);
      toast.success(data.invitation_sent ? `Invitation emailed to ${data.member.email} — ${gmail ? "they can just sign in with Google, no password needed" : "they sign in with Google or set a password"} (link expires in 7 days).` : `Member added. Email could not be sent — resend the invitation from the team list.`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const onAction = async (kind, m, arg) => {
    try {
      if (kind === "role") { if (arg === m.role) return; await api.put(`/team/${m.id}`, { role: arg }); toast.success(`${m.name} is now ${arg}`); }
      if (kind === "perm") {
        setMembers((list) => list.map((x) => (x.id === m.id ? { ...x, permissions: { ...x.permissions, ...arg } } : x)));
        await api.put(`/team/${m.id}`, { permissions: arg }); toast.success("Permissions updated");
      }
      if (kind === "active") { await api.put(`/team/${m.id}`, { is_active: m.is_active === false }); toast.success(m.is_active === false ? "Account activated" : "Account deactivated"); }
      if (kind === "reset") { const { data } = await api.post(`/team/${m.id}/reset-password`); setCred({ email: m.email, password: data.temporary_password }); }
      if (kind === "remove") { if (!window.confirm(`Remove ${m.name} from your team?`)) return; await api.delete(`/team/${m.id}`); toast.success("Removed"); }
      load();
    } catch (err) { toast.error(errorMessage(err)); load(); }
  };

  const toggleRest = (id) => setForm((f) => ({ ...f, restaurant_ids: f.restaurant_ids.includes(id) ? f.restaurant_ids.filter((x) => x !== id) : [...f.restaurant_ids, id] }));
  const roleOptions = [["staff", "Staff", "Scan invoices & place orders"], ["admin", "Admin", "Owner-like access you can limit"], ["owner", "Owner", "Full access, cannot be restricted"]].filter(([v]) => isTrueOwner || v === "staff");

  return (
    <div data-testid="team-page" className="max-w-3xl">
      <PageHeader title="Team" subtitle={isTrueOwner ? "Staff scan & order. Admins get owner-like access that you can restrict or revoke at any time." : "You can add and manage staff members."}
        actions={<Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="add-member-button"><UserPlus className="h-4 w-4 mr-2" /> Add team member</Button>} />

      {members === null ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="space-y-3 fade-up" data-testid="member-list">
          {members.map((m) => <MemberCard key={m.id} m={m} me={user} isTrueOwner={isTrueOwner} restaurants={restaurants} onAction={onAction} onEditSites={setEditSites} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add team member</DialogTitle><DialogDescription>You'll get a temporary password to share with them.</DialogDescription></DialogHeader>
          <form onSubmit={add} className="space-y-3" data-testid="add-member-form">
            <div className="space-y-1"><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" className="h-11" /></div>
            <div className="space-y-1"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" className="h-11" /></div>
            <div className="space-y-1"><Label>Password <span className="text-slate-400 font-normal">(leave blank to generate)</span></Label><Input value={form.password} minLength={8} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-password-input" className="h-11" /></div>
            <div className="space-y-1">
              <Label>Role</Label>
              <div className={`grid gap-2 ${roleOptions.length === 3 ? "grid-cols-3" : "grid-cols-1"}`}>
                {roleOptions.map(([v, l, d]) => (
                  <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`role-option-${v}`} className={`text-left rounded-lg border p-3 transition-colors ${form.role === v ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                    <p className="font-medium text-sm">{l}</p><p className="text-xs text-slate-500">{d}</p>
                  </button>
                ))}
              </div>
            </div>
            {form.role === "admin" && (
              <div className="space-y-1"><Label>Admin permissions</Label><PermissionToggles value={form.permissions} onChange={(k, v) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: v } }))} idPrefix="new" /></div>
            )}
            {restaurants.length > 1 && (
              <div className="space-y-1">
                <Label>Restaurants <span className="text-slate-400 font-normal">(none selected = all)</span></Label>
                <div className="flex flex-wrap gap-2">
                  {restaurants.map((r) => <button type="button" key={r.id} onClick={() => toggleRest(r.id)} data-testid={`member-restaurant-${r.id}`} className={`rounded-full border px-3 py-1.5 text-sm ${form.restaurant_ids.includes(r.id) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>{r.name}</button>)}
                </div>
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900" data-testid="member-save-button">{busy ? "Adding…" : "Add member"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {cred && <Credentials cred={cred} onClose={() => setCred(null)} />}
      {editSites && <RestaurantPicker m={editSites} restaurants={restaurants} onClose={() => setEditSites(null)} onSaved={load} />}
    </div>
  );
}
